if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      threeColumnSource = undefined;
      threeColumnMiddle = undefined;
      threeColumnRight = undefined;
      threeColumnBlocks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.product-form__quantity .quantity__input');
        this.syncThreeColumnLayout = this.syncThreeColumnLayout.bind(this);
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initThreeColumnLayout();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      initThreeColumnLayout() {
        const threeColumnContainer = this.querySelector('.product__info-container--three-column');
        if (!threeColumnContainer) return;

        this.threeColumnSource = threeColumnContainer.querySelector('.product__info-source');
        this.threeColumnMiddle = threeColumnContainer.querySelector('.product__info-columns--middle');
        this.threeColumnRight = threeColumnContainer.querySelector('.product__info-columns--right');

        if (!this.threeColumnSource || !this.threeColumnMiddle || !this.threeColumnRight) return;

        this.threeColumnBlocks = Array.from(this.threeColumnSource.children);
        this.syncThreeColumnLayout();
      }

      syncThreeColumnLayout() {
        if (!this.threeColumnSource || !this.threeColumnMiddle || !this.threeColumnRight) return;

        const isDesktop = window.matchMedia('(min-width: 990px)').matches;

        if (isDesktop) {
          this.threeColumnBlocks.forEach((block) => {
            if (block.classList.contains('product__info-block--right')) {
              this.threeColumnRight.appendChild(block);
            } else {
              this.threeColumnMiddle.appendChild(block);
            }
          });
          this.classList.add('product-info--three-column-ready');
        } else {
          this.threeColumnBlocks.forEach((block) => {
            this.threeColumnSource.appendChild(block);
          });
          this.classList.remove('product-info--three-column-ready');
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('title');
          updateSourceFromDestination('price');
          updateSourceFromDestination('Afterpay');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        this.quantityInput =
          this.querySelector('.product-form__quantity .quantity__input') || this.querySelector('.quantity__input');
        if (!this.quantityInput) return;

        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('pdp-countdown-timer')) {
  customElements.define(
    'pdp-countdown-timer',
    class PdpCountdownTimer extends HTMLElement {
      connectedCallback() {
        this.messageEl = this.querySelector('[data-pdp-timer-message]');
        this.valueEls = {
          days: this.querySelector('[data-timer-value="days"]'),
          hours: this.querySelector('[data-timer-value="hours"]'),
          minutes: this.querySelector('[data-timer-value="minutes"]'),
          seconds: this.querySelector('[data-timer-value="seconds"]'),
        };

        this.tick = this.tick.bind(this);
        this.tick();
        if (this.dataset.showTimer !== 'false') {
          this.intervalId = window.setInterval(this.tick, 1000);
        }
      }

      disconnectedCallback() {
        if (this.intervalId) {
          window.clearInterval(this.intervalId);
          this.intervalId = null;
        }
      }

      parseTimerDate(value, isEndDate = false) {
        const rawValue = String(value || '').trim();
        if (!rawValue) return null;

        if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
          return new Date(`${rawValue}T${isEndDate ? '23:59:59' : '00:00:00'}`);
        }

        if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(rawValue)) {
          return new Date(rawValue.replace(' ', 'T'));
        }

        return new Date(rawValue);
      }

      formatUnit(value) {
        return String(Math.max(0, Math.floor(value))).padStart(2, '0');
      }

      updateMessage(text) {
        if (this.messageEl) {
          this.messageEl.textContent = text;
        }
      }

      updateValues(remainingMs) {
        const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (this.valueEls.days) this.valueEls.days.textContent = this.formatUnit(days);
        if (this.valueEls.hours) this.valueEls.hours.textContent = this.formatUnit(hours);
        if (this.valueEls.minutes) this.valueEls.minutes.textContent = this.formatUnit(minutes);
        if (this.valueEls.seconds) this.valueEls.seconds.textContent = this.formatUnit(seconds);
      }

      tick() {
        if (this.dataset.showTimer === 'false') {
          this.hidden = false;
          return;
        }

        const startDate = this.parseTimerDate(this.dataset.startDatetime);
        const endDate = this.parseTimerDate(this.dataset.endDatetime, true);
        const upcomingLabel = this.dataset.upcomingLabel || 'Starts in';
        const liveLabel = this.dataset.liveLabel || 'Ends in';

        if (!endDate || Number.isNaN(endDate.getTime())) {
          this.hidden = true;
          return;
        }

        if (startDate && Number.isNaN(startDate.getTime())) {
          this.hidden = true;
          return;
        }

        const now = new Date();

        if (startDate && now < startDate) {
          this.hidden = true;
          this.updateMessage(upcomingLabel);
          this.updateValues(startDate.getTime() - now.getTime());
          return;
        }

        if (now >= endDate) {
          this.hidden = true;
          return;
        }

        this.hidden = false;
        this.updateMessage(liveLabel);
        this.updateValues(endDate.getTime() - now.getTime());
      }
    }
  );
}

(function () {
  const WIDGET_SELECTOR = '[data-volume-discount-root]';
  const QUANTITY_SELECTOR = [
    'product-info .product-form__quantity .quantity__input',
    'product-info input[name="quantity"]',
    '#sticky-atc-quantity',
    '.sticky_atc_wrapper input[name="quantity"]',
  ].join(', ');

  const widgetState = new WeakMap();
  let renderTimer = null;
  let listenersBound = false;
  let syncingQuantity = false;

  function scheduleRender() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(renderAllWidgets, 0);
  }

  function parseJSON(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  function normalizeCountryCode(value) {
    const code = String(value || '').trim().toUpperCase();
    return code || 'DEFAULT';
  }

  function normalizeTier(rawTier, index) {
    if (!rawTier || typeof rawTier !== 'object') return null;

    const discountType = String(rawTier.discount_type || rawTier.discountType || '').toLowerCase();
    const discountValue = Number(rawTier.discount_value ?? rawTier.discountValue);
    const minQuantity = Number(rawTier.min_quantity ?? rawTier.minQty);
    const rawMaxQuantity = rawTier.max_quantity ?? rawTier.maxQty;
    const maxQuantity =
      rawMaxQuantity === null || rawMaxQuantity === '' || rawMaxQuantity === undefined
        ? null
        : Number(rawMaxQuantity);

    if (discountType && discountType !== 'percentage') return null;
    if (!Number.isFinite(minQuantity) || minQuantity < 1) return null;
    if (!Number.isFinite(discountValue) || discountValue <= 0) return null;
    if (maxQuantity !== null && (!Number.isFinite(maxQuantity) || maxQuantity < minQuantity)) return null;

    return {
      index: String(index),
      min: Math.floor(minQuantity),
      max: maxQuantity === null ? null : Math.floor(maxQuantity),
      discount: discountValue,
      key: [
        Math.floor(minQuantity),
        maxQuantity === null ? '' : Math.floor(maxQuantity),
        discountType || 'percentage',
        discountValue,
      ].join(':'),
    };
  }

  function normalizeTiers(rawTiers) {
    if (!Array.isArray(rawTiers)) return [];

    return rawTiers
      .map((tier, index) => normalizeTier(tier, index))
      .filter(Boolean)
      .sort((a, b) => a.min - b.min || (a.max || Infinity) - (b.max || Infinity));
  }

  function resolveTierSetFromScope(scope, countryCode) {
    if (!scope || typeof scope !== 'object') return [];

    const tiersByCountry =
      scope.tiers_by_country && typeof scope.tiers_by_country === 'object' ? scope.tiers_by_country : null;

    if (tiersByCountry) {
      if (Object.prototype.hasOwnProperty.call(tiersByCountry, countryCode)) {
        return normalizeTiers(tiersByCountry[countryCode]);
      }

      if (Object.prototype.hasOwnProperty.call(tiersByCountry, 'default')) {
        return normalizeTiers(tiersByCountry.default);
      }
    }

    return normalizeTiers(scope.tiers);
  }

  function hasVariantSpecificConfig(entry) {
    if (!entry || typeof entry !== 'object') return false;
    return Boolean(
      (entry.override && entry.override.is_overridden) ||
        Array.isArray(entry.tiers) ||
        (entry.tiers_by_country && typeof entry.tiers_by_country === 'object')
    );
  }

  function getWidgetState(widget) {
    if (widgetState.has(widget)) {
      return widgetState.get(widget);
    }

    const configScript = widget.querySelector('[data-volume-discount-config]');
    const parsedConfig = configScript ? parseJSON(configScript.textContent || '') : null;
    const state = { config: parsedConfig };

    widgetState.set(widget, state);
    return state;
  }

  function syncWidgetVariantData(widget, variant) {
    if (!widget || !variant) return;

    const basePrice = Number(variant.price || 0);
    const compareAtPrice = Number(variant.compare_at_price || 0);

    widget.dataset.volumeDiscountCurrentVariant = String(variant.id || '');
    widget.dataset.volumeDiscountInventory = String(variant.inventory_quantity ?? '');
    widget.dataset.volumeDiscountBasePrice = String(basePrice);
    widget.dataset.volumeDiscountBaseCompare = String(compareAtPrice > basePrice ? compareAtPrice : basePrice);
  }

  function getProductInfoBySectionId(sectionId) {
    if (!sectionId) return null;
    return document.querySelector(`product-info[data-section="${CSS.escape(String(sectionId))}"]`);
  }

  function getSelectedVariantFromProductInfo(productInfo) {
    if (!productInfo) return null;

    const selectedVariantScript = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!selectedVariantScript) return null;

    return parseJSON(selectedVariantScript.textContent || '');
  }

  function syncWidgetsForVariant(variant, sectionId) {
    if (!variant) return;

    const widgets = [];
    const productInfo = getProductInfoBySectionId(sectionId);

    if (productInfo) {
      widgets.push(...productInfo.querySelectorAll(WIDGET_SELECTOR));
    }

    const stickyWidget = document.querySelector('#sticky-atc-wrapper [data-volume-discount-root]');
    if (stickyWidget) {
      const productId = productInfo?.dataset.productId || '';
      if (!productId || stickyWidget.dataset.volumeDiscountProduct === productId) {
        widgets.push(stickyWidget);
      }
    }

    widgets.forEach((widget) => syncWidgetVariantData(widget, variant));
  }

  function getPriceContainer(widget) {
    const directContainer = widget.closest('[data-volume-discount-price-container]');
    if (directContainer) return directContainer;

    const productId = widget.dataset.volumeDiscountProduct;
    const productInfo = widget.closest('product-info');
    if (productInfo) {
      const scopedContainer = productInfo.querySelector('[data-volume-discount-price-container]');
      if (scopedContainer) return scopedContainer;
    }

    const stickyWrapper = widget.closest('#sticky-atc-wrapper, .sticky_atc_wrapper');
    if (stickyWrapper) {
      const stickyContainer = stickyWrapper.querySelector('[data-volume-discount-price-container]');
      if (stickyContainer) return stickyContainer;
    }

    if (productId) {
      const matchedContainer = document.querySelector(
        `[data-volume-discount-root][data-volume-discount-product="${CSS.escape(productId)}"]`
      )?.closest('product-info')?.querySelector('[data-volume-discount-price-container]');

      if (matchedContainer) return matchedContainer;
    }

    return widget.parentElement;
  }

  function getQuantityInput(widget) {
    const localContainer =
      widget.closest('product-info') ||
      widget.closest('#sticky-atc-wrapper') ||
      widget.closest('.sticky_atc_wrapper') ||
      document;

    return (
      localContainer.querySelector('.product-form__quantity .quantity__input') ||
      localContainer.querySelector('.product-form__quantity input[name="quantity"]') ||
      localContainer.querySelector('#sticky-atc-quantity') ||
      localContainer.querySelector('input[name="quantity"]') ||
      document.querySelector('product-info .product-form__quantity .quantity__input') ||
      document.querySelector('#sticky-atc-quantity') ||
      document.querySelector('input[name="quantity"]')
    );
  }

  function getQuantity(widget) {
    const input = getQuantityInput(widget);
    const min = input ? Number(input.min || 1) : 1;
    const value = input ? Number(input.value || min) : 1;
    return Math.max(min, Number.isFinite(value) ? value : min);
  }

  function getMaxQuantity(widget) {
    const input = getQuantityInput(widget);
    const inputMax = input && input.max !== '' ? Number(input.max) : Infinity;
    const shopMax = Number(widget?.dataset.volumeDiscountShopLimit);
    const inventoryQuantity = Number(widget?.dataset.volumeDiscountInventory);
    const maxValues = [inputMax, shopMax, inventoryQuantity].filter(Number.isFinite);
    const maxQuantity = maxValues.length ? Math.min(...maxValues) : Infinity;

    return Number.isFinite(maxQuantity) ? maxQuantity : Infinity;
  }

  function setQuantityInputs(qty) {
    if (syncingQuantity) return;

    syncingQuantity = true;
    const normalizedQty = Math.max(1, Number(qty) || 1);

    document.querySelectorAll(QUANTITY_SELECTOR).forEach((input) => {
      const max = input.max === '' ? Infinity : Number(input.max);
      input.value = Number.isFinite(max) ? Math.min(normalizedQty, max) : normalizedQty;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    syncingQuantity = false;
    scheduleRender();
  }

  function getCurrentVariantId(widget) {
    const directValue = widget.dataset.volumeDiscountCurrentVariant;
    if (directValue) return String(directValue);

    const productInfo = widget.closest('product-info');
    const stickyVariantSelect = widget.closest('#sticky-atc-wrapper')?.querySelector('#sticky-atc-variant');
    const hiddenVariantInput = productInfo?.querySelector('input[name="id"]');

    if (stickyVariantSelect && stickyVariantSelect.value) {
      return String(stickyVariantSelect.value);
    }

    if (hiddenVariantInput && hiddenVariantInput.value) {
      return String(hiddenVariantInput.value);
    }

    return '';
  }

  function getMoneyFormat(widget) {
    const useCurrency = widget.dataset.volumeDiscountCurrencyCodeEnabled === 'true';
    const withCurrency = widget.dataset.volumeDiscountMoneyWithCurrencyFormat || widget.dataset.volumeDiscountMoneyFormat;
    const plain = widget.dataset.volumeDiscountMoneyFormat || withCurrency;
    return useCurrency ? withCurrency : plain;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (Number.isNaN(number) || number === null) return '0';

    const parts = (number / 100).toFixed(precision).split('.');
    const integerPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return integerPart + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, widget) {
    const format = getMoneyFormat(widget) || '${{amount}}';
    const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

    let formatted = '0';
    switch (placeholder) {
      case 'amount':
      case 'amount_with_currency':
        formatted = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        formatted = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(cents, 2, ',', '.');
        break;
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  function getRoundedDiscountPercent(comparePrice, finalPrice) {
    if (!comparePrice || comparePrice <= finalPrice) return 0;

    // Round to the nearest whole percent so the badge matches the displayed tier math.
    return Math.round(((comparePrice - finalPrice) * 100) / comparePrice);
  }

  function findTierByQuantity(quantity, tiers) {
    let matched = null;
    (tiers || []).forEach((tier) => {
      if (quantity >= tier.min && (tier.max === null || quantity <= tier.max)) {
        matched = tier;
      }
    });
    return matched;
  }

  function getBaseState(widget) {
    const basePrice = Number(widget?.dataset.volumeDiscountBasePrice || 0);
    const compare = Number(widget?.dataset.volumeDiscountBaseCompare || basePrice);
    return { price: basePrice, compare };
  }

  function calculateTierPricing(widget, tier) {
    const { price: basePrice, compare: baseCompare } = getBaseState(widget);
    const saleActive = baseCompare > basePrice;
    const baseDiscountPercent = saleActive ? getRoundedDiscountPercent(baseCompare, basePrice) : 0;
    const tierDiscount = tier ? Number(tier.discount || 0) : 0;
    const combinedDiscountPercent = tier ? Math.min(100, baseDiscountPercent + tierDiscount) : baseDiscountPercent;
    const pricingBase = saleActive ? baseCompare : basePrice;
    const finalPrice = tier ? Math.round((pricingBase * (100 - combinedDiscountPercent)) / 100) : basePrice;
    const comparePrice = baseCompare > finalPrice ? baseCompare : basePrice;
    const savings = Math.max(0, comparePrice - finalPrice);

    return {
      basePrice,
      baseCompare,
      baseDiscountPercent,
      comparePrice,
      combinedDiscountPercent,
      finalPrice,
      displayDiscountPercent: combinedDiscountPercent,
      pricingBase,
      saleActive,
      savings,
      tierDiscount,
    };
  }

  function getResolvedTiers(widget) {
    const state = getWidgetState(widget);
    const config = state.config;
    if (!config) return [];

    const countryCode = normalizeCountryCode(widget.dataset.volumeDiscountCountry);
    const variantId = getCurrentVariantId(widget);
    const variants = config.variants && typeof config.variants === 'object' ? config.variants : {};
    const variantEntry = variantId ? variants[variantId] : null;

    if (hasVariantSpecificConfig(variantEntry)) {
      return resolveTierSetFromScope(variantEntry, countryCode);
    }

    return resolveTierSetFromScope(config, countryCode);
  }

  function updatePriceContainer(widget, finalPrice, comparePrice, discountPercent) {
    const container = getPriceContainer(widget);
    if (!container) return;

    const price = container.querySelector('.price');
    if (!price) return;

    const isOnSale = comparePrice > finalPrice;
    const finalMoney = formatMoney(finalPrice, widget);
    const compareMoney = formatMoney(comparePrice, widget);

    price.classList.toggle('price--on-sale', isOnSale);

    const salePriceNode = price.querySelector('.price__sale .price-item--sale');
    const comparePriceNode = price.querySelector('.price__sale .price-item--regular');
    const regularPriceNode = price.querySelector('.price__regular .price-item--regular');
    const discountNode = price.querySelector('[data-price-discount]');

    if (salePriceNode) salePriceNode.innerHTML = finalMoney;
    if (comparePriceNode) comparePriceNode.innerHTML = compareMoney;
    if (regularPriceNode) regularPriceNode.innerHTML = isOnSale ? compareMoney : finalMoney;

    if (discountNode) {
      if (isOnSale) {
        const safeDiscountPercent = Number.isFinite(discountPercent) ? Math.max(0, Math.round(discountPercent)) : 0;
        discountNode.innerHTML = `-${safeDiscountPercent}% <span class="discount-off-text">OFF</span>`;
        discountNode.hidden = false;
      } else {
        discountNode.textContent = '';
        discountNode.hidden = true;
      }
    }
  }

  function closeVolumeDiscountMenu(root) {
    if (!root) return;

    const state = getWidgetState(root);
    const ui = state.ui || root.querySelector('[data-volume-discount-ui]');
    if (!ui) return;

    ui.classList.remove('is-open');

    const trigger = ui.querySelector('[data-volume-discount-trigger]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    const overlay = state.overlay || ui.querySelector('.volume-discount__overlay');
    const menu = state.menu || ui.querySelector('.volume-discount__menu');

    overlay?.classList.remove('is-open');
    menu?.classList.remove('is-open');

    if (overlay && overlay.parentElement !== ui) {
      ui.insertBefore(overlay, trigger || ui.firstChild);
    }

    if (menu && menu.parentElement !== ui) {
      ui.appendChild(menu);
    }

    document.body.classList.remove('vd-no-scroll');
  }

  function closeAllVolumeDiscountMenus(exceptRoot = null) {
    document.querySelectorAll(WIDGET_SELECTOR).forEach((root) => {
      if (exceptRoot && root === exceptRoot) return;
      closeVolumeDiscountMenu(root);
    });
  }

  function toggleVolumeDiscountMenu(root) {
    if (!root) return;

    const state = getWidgetState(root);
    const ui = state.ui || root.querySelector('[data-volume-discount-ui]');
    const trigger = ui?.querySelector('[data-volume-discount-trigger]');
    if (!ui || !trigger) return;

    const isOpen = ui.classList.contains('is-open');
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    closeAllVolumeDiscountMenus(root);

    if (!isOpen) {
      const overlay = state.overlay || ui.querySelector('.volume-discount__overlay');
      const menu = state.menu || ui.querySelector('.volume-discount__menu');

      ui.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');

      if (isMobile) {
        if (overlay && overlay.parentElement !== document.body) {
          document.body.appendChild(overlay);
        }

        if (menu && menu.parentElement !== document.body) {
          document.body.appendChild(menu);
        }

        overlay?.classList.add('is-open');
        menu?.classList.add('is-open');
        document.body.classList.add('vd-no-scroll');
      }
    } else {
      closeVolumeDiscountMenu(root);
    }
  }

  function ensureVolumeDiscountUi(root) {
    const container = root.querySelector('.volume-discount__select-label') || root;
    let ui = root.querySelector('[data-volume-discount-ui]');

    if (!ui) {
      ui = document.createElement('div');
      ui.className = 'volume-discount__custom-select';
      ui.dataset.volumeDiscountUi = 'true';

      const overlay = document.createElement('div');
      overlay.className = 'volume-discount__overlay';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'volume-discount__trigger';
      trigger.setAttribute('data-volume-discount-trigger', 'true');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');

      const triggerValue = document.createElement('span');
      triggerValue.className = 'volume-discount__trigger-value';

      const arrow = document.createElement('span');
      arrow.className = 'volume-discount__arrow';
      arrow.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M5 7l5 5 5-5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"></path>
        </svg>
      `;

      const menu = document.createElement('div');
      menu.className = 'volume-discount__menu';
      menu.setAttribute('role', 'listbox');

      overlay.addEventListener('click', () => {
        closeVolumeDiscountMenu(root);
      });

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        toggleVolumeDiscountMenu(root);
      });

      ui.appendChild(overlay);
      ui.appendChild(trigger);
      trigger.appendChild(triggerValue);
      trigger.appendChild(arrow);
      ui.appendChild(menu);
      container.appendChild(ui);

      const state = getWidgetState(root);
      state.ui = ui;
      state.overlay = overlay;
      state.menu = menu;
    } else {
      const state = getWidgetState(root);
      state.ui = ui;
      state.overlay = ui.querySelector('.volume-discount__overlay') || state.overlay;
      state.menu = ui.querySelector('.volume-discount__menu') || state.menu;
    }

    return ui;
  }

  function buildVolumeDiscountCustomSelect(widget, select, activeTier, quantity, tiers) {
    if (!widget || !select) return;

    const ui = ensureVolumeDiscountUi(widget);
    const trigger = ui.querySelector('[data-volume-discount-trigger]');
    const triggerValue = ui.querySelector('.volume-discount__trigger-value');
    const menu = ui.querySelector('.volume-discount__menu');
    const placeholderOption = select.options[0];
    const placeholderText = placeholderOption
      ? String(placeholderOption.textContent || '').trim()
      : 'Buy More Save More';
    const selectedOption = select.options[select.selectedIndex] || placeholderOption;
    const selectedText =
      selectedOption && selectedOption.value ? String(selectedOption.textContent || '').trim() : placeholderText;

    select.classList.add('native-select-hidden');

    if (trigger) {
      trigger.classList.toggle('has-selection', Boolean(activeTier && Number(activeTier.discount) > 0));
    }

    if (triggerValue) {
      if (activeTier && Number(activeTier.discount) > 0) {
        const pricing = calculateTierPricing(widget, activeTier);
        triggerValue.innerHTML = `
          <span class="vd-selected">
            <span class="vd-selected-save">Savings ${formatMoney(pricing.savings, widget)}</span>
            <span class="vd-selected-off">${activeTier.discount}%</span>
          </span>
        `;
      } else {
        triggerValue.textContent = selectedText || placeholderText;
      }
    }

    if (!menu) return;

    menu.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'vd-sheet-header';
    header.innerHTML = `
      <div class="vd-header-inner">
        <span class="vd-header-title">Select Quantity</span>
        <button type="button" class="vd-close-btn" aria-label="Close discounts">&times;</button>
      </div>
    `;
    menu.appendChild(header);

    const closeBtn = header.querySelector('.vd-close-btn');
    const closeSheet = () => closeVolumeDiscountMenu(widget);

    closeBtn?.addEventListener('click', closeSheet);

    tiers.forEach((tier, index) => {
      const option = select.options[index + 1];
      if (!option || !option.value) return;

      const pricing = calculateTierPricing(widget, tier);
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'volume-discount__menu-option';
      item.dataset.tierValue = option.value;

      if (option.disabled) {
        item.disabled = true;
        item.classList.add('disabled');
      }

      if (activeTier && option.value === activeTier.key) {
        item.classList.add('selected');
        item.setAttribute('aria-selected', 'true');
      }

      item.innerHTML = `
        <div class="vd-row">
          <div class="vd-left">
            <strong>${tier.min}${tier.max !== null ? `-${tier.max}` : '+'}</strong>
            <span class="vd-price">${formatMoney(pricing.finalPrice, widget)} /qty</span>
          </div>
          <div class="vd-right">
            <span class="vd-save">Save ${formatMoney(pricing.savings, widget)}</span>
            <span class="vd-off">${tier.discount}%</span>
          </div>
        </div>
      `;

      item.addEventListener('click', () => {
        if (option.disabled) return;

        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        closeVolumeDiscountMenu(widget);
      });

      menu.appendChild(item);
    });

    const existingCustomRow = menu.querySelector('.vd-custom-qty-row');
    if (existingCustomRow) existingCustomRow.remove();

    const customRow = document.createElement('div');
    customRow.className = 'vd-custom-qty-row';
    customRow.innerHTML = `
      <div class="vd-custom-row">
        <div class="vd-left">Custom Qty:</div>
        <div class="vd-right">
          <input type="number" min="1" class="vd-custom-input" />
          <button type="button" class="vd-custom-apply">Apply</button>
        </div>
      </div>
    `;
    menu.appendChild(customRow);

    const customInput = customRow.querySelector('.vd-custom-input');
    const customApply = customRow.querySelector('.vd-custom-apply');
    const maxQuantity = getMaxQuantity(widget);
    if (customInput) {
      customInput.value = quantity || getQuantity(widget) || 1;
      if (Number.isFinite(maxQuantity)) {
        customInput.max = maxQuantity;
      }
    }

    customInput?.addEventListener('input', () => {
      const value = Number(customInput.value);
      if (Number.isFinite(maxQuantity) && value > maxQuantity) {
        customInput.value = maxQuantity;
      }
    });

    customApply?.addEventListener('click', () => {
      const nextQty = Number(customInput?.value || quantity || 1);
      setQuantityInputs(nextQty);
      closeVolumeDiscountMenu(widget);
    });

    customInput?.addEventListener('keypress', (event) => {
      if (event.key === 'Enter') {
        customApply?.click();
      }
    });
  }

  function updateWidgetMessage(widget, quantity, activeTier, tiers) {
    const message = widget.querySelector('[data-volume-discount-message]');
    if (!message) return;

    if (!tiers || tiers.length === 0) {
      message.textContent = '';
      message.hidden = true;
      widget.hidden = true;
      return;
    }

    widget.hidden = false;

    const nextTier = tiers.find((tier) => tier.min > quantity);
    let parts = [];

    if (!activeTier) {
      if (!nextTier) {
        message.textContent = '';
        message.hidden = true;
        return;
      }

      const remaining = Math.max(1, nextTier.min - quantity);
      parts = [
        { type: 'text', text: 'Add' },
        { type: 'off', text: String(remaining) },
        { type: 'text', text: 'more to unlock' },
        { type: 'off', text: `${nextTier.discount}% off` },
      ];
    } else if (!nextTier) {
      parts = [
        { type: 'text', text: 'You unlocked' },
        { type: 'off', text: `${activeTier.discount}% OFF` },
      ];
    } else {
      const remaining = Math.max(1, nextTier.min - quantity);
      parts = [
        { type: 'text', text: 'You unlocked' },
        { type: 'off', text: `${activeTier.discount}% OFF` },
        { type: 'text', text: 'Add' },
        { type: 'off', text: String(remaining) },
        { type: 'text', text: 'more to unlock' },
        { type: 'off', text: `${nextTier.discount}% off` },
      ];
    }

    message.innerHTML = parts.map((part) => `<span class="discount-display-${part.type}">${part.text}</span>`).join(' ');
    message.hidden = false;
  }

  function updateWidgetSelect(widget, activeTier, tiers, quantity) {
    const select = widget.querySelector('[data-volume-discount-select]');
    if (!select) return;

    const maxQty = getMaxQuantity(widget);
    const currentTierKey = activeTier ? activeTier.key : '';

    select.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Buy more Save more';
    select.appendChild(placeholder);

    tiers.forEach((tier) => {
      const option = document.createElement('option');
      option.value = tier.key;
      option.dataset.minQty = String(tier.min);
      option.dataset.maxQty = tier.max === null ? '' : String(tier.max);
      option.dataset.discountValue = String(tier.discount);
      option.textContent = `Buy ${tier.min}${tier.max !== null ? `-${tier.max}` : '+'} and Save ${tier.discount}%`;
      option.disabled = tier.min > maxQty;

      if (tier.key === currentTierKey) {
        option.selected = true;
      }

      select.appendChild(option);
    });

    select.value = currentTierKey || '';
    select.dataset.currentQuantity = String(quantity);
    buildVolumeDiscountCustomSelect(widget, select, activeTier, quantity, tiers);
  }

  function renderWidget(widget) {
    const state = getWidgetState(widget);
    if (!state.config) {
      widget.hidden = true;
      return;
    }

    const tiers = getResolvedTiers(widget);
    const quantity = getQuantity(widget);
    const activeTier = findTierByQuantity(quantity, tiers);
    const pricing = calculateTierPricing(widget, activeTier);

    updatePriceContainer(widget, pricing.finalPrice, pricing.comparePrice, pricing.displayDiscountPercent);
    updateWidgetMessage(widget, quantity, activeTier, tiers);
    updateWidgetSelect(widget, activeTier, tiers, quantity);
  }

  function renderAllWidgets() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(renderWidget);
  }

  function syncQuantityFromSelect(select) {
    const widget = select.closest(WIDGET_SELECTOR);
    if (!widget) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption || !selectedOption.value) {
      scheduleRender();
      return;
    }

    const tier = {
      key: selectedOption.value,
      min: Number(selectedOption.dataset.minQty || 0),
      max: selectedOption.dataset.maxQty ? Number(selectedOption.dataset.maxQty) : null,
      discount: Number(selectedOption.dataset.discountValue || 0),
    };

    if (!tier.min) {
      scheduleRender();
      return;
    }

    setQuantityInputs(tier.min);
    scheduleRender();
  }

  function onDocumentChange(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.matches('[data-volume-discount-select]')) {
      syncQuantityFromSelect(event.target);
      return;
    }

    if (event.target.matches(QUANTITY_SELECTOR) || event.target.closest(QUANTITY_SELECTOR)) {
      if (syncingQuantity) return;
      scheduleRender();
    }
  }

  function onDocumentInput(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.matches(QUANTITY_SELECTOR) || event.target.closest(QUANTITY_SELECTOR)) {
      if (syncingQuantity) return;
      scheduleRender();
    }
  }

  function onVolumeDiscountDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-volume-discount-trigger]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();

      const root = trigger.closest(WIDGET_SELECTOR);
      toggleVolumeDiscountMenu(root);
      return;
    }

    if (event.target.closest('[data-volume-discount-ui]')) return;

    closeAllVolumeDiscountMenus();
  }

  function onVolumeDiscountKeydown(event) {
    if (event.key !== 'Escape') return;

    closeAllVolumeDiscountMenus();
  }

  function bindListeners() {
    if (listenersBound) return;

    document.addEventListener('change', onDocumentChange, true);
    document.addEventListener('input', onDocumentInput, true);
    document.addEventListener('click', onVolumeDiscountDocumentClick, true);
    document.addEventListener('keydown', onVolumeDiscountKeydown, true);
    document.addEventListener(
      'product-info:loaded',
      (event) => {
        const productInfo = event.target instanceof Element ? event.target : null;
        const variant = getSelectedVariantFromProductInfo(productInfo);
        const sectionId = productInfo?.dataset.section;

        if (variant) {
          syncWidgetsForVariant(variant, sectionId);
        }

        scheduleRender();
      },
      true
    );

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        const sectionId = event?.data?.sectionId;

        syncWidgetsForVariant(variant, sectionId);
        setTimeout(scheduleRender, 0);
      });
    }

    listenersBound = true;
  }

  function init() {
    bindListeners();
    renderAllWidgets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
